import { animate } from 'motion'
import { useReducedMotion } from 'motion/react'
import { useEffect, useState } from 'react'

import { tourTargetSelector } from './tourBox'

// Slow enough that the user can follow where the page is going.
const SCROLL_SECONDS = 1.2
// Closer than this many pixels counts as already in place, so the card shows straight away.
const ALREADY_THERE_PIXELS = 1

const pageScroller = () =>
  (document.scrollingElement ?? document.documentElement) as HTMLElement

const isScrollable = (element: HTMLElement) => {
  const { overflowY } = getComputedStyle(element)
  const canScroll = overflowY === 'auto' || overflowY === 'scroll'
  return canScroll && element.scrollHeight > element.clientHeight
}

const scrollContainerOf = (element: HTMLElement): HTMLElement => {
  for (
    let parent = element.parentElement;
    parent;
    parent = parent.parentElement
  ) {
    if (isScrollable(parent)) return parent
  }
  return pageScroller()
}

// The scrollTop that puts the area in the middle of its container's visible part.
const centredScrollTop = (element: HTMLElement, container: HTMLElement) => {
  const area = element.getBoundingClientRect()
  const isPage = container === pageScroller()
  const viewTop = isPage ? 0 : container.getBoundingClientRect().top
  const viewHeight = isPage ? window.innerHeight : container.clientHeight
  const offset = area.top + area.height / 2 - (viewTop + viewHeight / 2)
  const furthest = container.scrollHeight - container.clientHeight
  return Math.min(Math.max(container.scrollTop + offset, 0), furthest)
}

// Glides the area to the middle of the screen once it is found, and reports settled when the
// glide ends, so the step card never shows while the page is still moving. A wheel or touch from
// the user stops the glide where it is.
export const useScrollIntoView = (target: string, isFound: boolean) => {
  const reduceMotion = useReducedMotion() ?? false
  const [isSettled, setIsSettled] = useState(false)

  useEffect(() => {
    if (!isFound) return
    const element = document.querySelector<HTMLElement>(
      tourTargetSelector(target),
    )
    if (!element) return
    const container = scrollContainerOf(element)
    const destination = centredScrollTop(element, container)
    const isInPlace =
      Math.abs(destination - container.scrollTop) < ALREADY_THERE_PIXELS
    if (reduceMotion || isInPlace) {
      container.scrollTop = destination
      setIsSettled(true)
      return
    }
    const glide = animate(container.scrollTop, destination, {
      duration: SCROLL_SECONDS,
      ease: 'easeInOut',
      onUpdate: (scrollTop) => {
        container.scrollTop = scrollTop
      },
      onComplete: () => setIsSettled(true),
    })
    const yieldToUser = () => {
      glide.stop()
      setIsSettled(true)
    }
    window.addEventListener('wheel', yieldToUser, { passive: true })
    window.addEventListener('touchstart', yieldToUser, { passive: true })
    return () => {
      glide.stop()
      window.removeEventListener('wheel', yieldToUser)
      window.removeEventListener('touchstart', yieldToUser)
    }
  }, [target, isFound, reduceMotion])

  return isSettled
}
